import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';
import {
  RefreshCw,
  Plus,
  Trash2,
  Send,
  History,
  Loader2,
  Webhook,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import {
  WEBHOOK_EVENTS,
  WEBHOOK_EVENT_LABELS,
  listWebhooks,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  listWebhookDeliveries,
  testWebhook,
  type WebhookSubscription,
  type WebhookDelivery,
} from '../api/webhooks';

const STATUS_LABELS: Record<string, string> = {
  pending: '待发送',
  success: '成功',
  failed: '失败',
};

export default function WebhookAdmin() {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [subscriptions, setSubscriptions] = useState<WebhookSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<Record<string, WebhookDelivery[]>>({});
  const [deliveriesOpen, setDeliveriesOpen] = useState<Record<string, boolean>>({});

  // 新建表单
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [events, setEvents] = useState<string[]>([...WEBHOOK_EVENTS]);

  // .then/.catch/.finally 链而非 async/await：setState 只发生在异步回调里，
  // 避免在 effect 体内同步 setState（react-hooks/set-state-in-effect）
  const loadData = useCallback(() => {
    return listWebhooks()
      .then((data) => setSubscriptions(data))
      .catch((e: unknown) => {
        console.error('Failed to load webhooks:', e);
        toast.error('加载 Webhook 订阅失败');
      })
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const toggleEvent = (event: string) => {
    setEvents(prev => (prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]));
  };

  const handleCreate = async () => {
    if (!name.trim() || !url.trim() || !secret.trim()) {
      toast.error('名称 / URL / 密钥为必填');
      return;
    }
    setCreating(true);
    try {
      await createWebhook({ name: name.trim(), url: url.trim(), secret: secret.trim(), events });
      toast.success('订阅已创建');
      setShowCreate(false);
      setName('');
      setUrl('');
      setSecret('');
      setEvents([...WEBHOOK_EVENTS]);
      await loadData();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      toast.error('创建失败: ' + (typeof err.response?.data?.detail === 'string' ? err.response.data.detail : '请检查参数'));
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (sub: WebhookSubscription) => {
    try {
      const updated = await updateWebhook(sub.id, { is_enabled: !sub.is_enabled });
      setSubscriptions(prev => prev.map(s => (s.id === sub.id ? updated : s)));
      toast.success(updated.is_enabled ? '订阅已启用' : '订阅已停用');
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      toast.error('操作失败: ' + (typeof err.response?.data?.detail === 'string' ? err.response.data.detail : '未知错误'));
    }
  };

  const handleDelete = async (sub: WebhookSubscription) => {
    if (!(await confirm({ title: `确定删除订阅「${sub.name}」吗？`, message: '投递记录将一并清除。', danger: true }))) return;
    try {
      await deleteWebhook(sub.id);
      setSubscriptions(prev => prev.filter(s => s.id !== sub.id));
      toast.success('订阅已删除');
    } catch {
      toast.error('删除失败');
    }
  };

  const handleTest = async (sub: WebhookSubscription) => {
    setTestingId(sub.id);
    try {
      const delivery = await testWebhook(sub.id);
      if (delivery.status === 'success') {
        toast.success(`测试发送成功（HTTP ${delivery.response_status ?? '-'}）`);
      } else {
        toast.error(`测试发送失败：${delivery.last_error || '未知错误'}`);
      }
    } catch {
      toast.error('测试发送调用失败');
    } finally {
      setTestingId(null);
    }
  };

  const toggleDeliveries = async (sub: WebhookSubscription) => {
    const open = !deliveriesOpen[sub.id];
    setDeliveriesOpen(prev => ({ ...prev, [sub.id]: open }));
    if (open) {
      try {
        const items = await listWebhookDeliveries(sub.id);
        setDeliveries(prev => ({ ...prev, [sub.id]: items }));
      } catch {
        toast.error('加载投递记录失败');
      }
    }
  };

  const statusColor = (status: string) => {
    if (status === 'success') return 'text-success';
    if (status === 'failed') return 'text-error';
    return 'text-warning';
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <h1><Webhook className="inline mr-2" size={24} /> Webhook 订阅</h1>
        <div className="flex gap-2">
          <button className="secondary" onClick={loadData}><RefreshCw size={16} className="inline mr-1" /> 刷新</button>
          <button className="primary" onClick={() => setShowCreate(v => !v)}>
            <Plus size={16} className="inline mr-1" /> 新建订阅
          </button>
        </div>
      </div>

      {showCreate && (
        <div className="card mb-6">
          <h3 className="mb-3">新建订阅</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>名称</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="如：生产告警群" />
            </div>
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>接收 URL</label>
              <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com/hook" />
            </div>
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                签名密钥（HMAC-SHA256，仅创建时可见，响应不回传明文）
              </label>
              <input value={secret} onChange={e => setSecret(e.target.value)} placeholder="自定义密钥，接收方用于验签" />
            </div>
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>事件类型（全选 = 订阅全部）</label>
              <div className="flex flex-wrap gap-2">
                {WEBHOOK_EVENTS.map(event => (
                  <label key={event} className="flex items-center gap-1 text-sm" style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={events.includes(event)} onChange={() => toggleEvent(event)} />
                    {WEBHOOK_EVENT_LABELS[event] ?? event}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button className="primary" onClick={handleCreate} disabled={creating}>
              {creating ? <><Loader2 size={16} className="inline animate-spin mr-1" /> 创建中...</> : '创建'}
            </button>
            <button className="secondary" onClick={() => setShowCreate(false)}>取消</button>
          </div>
        </div>
      )}

      {subscriptions.length === 0 ? (
        <div className="card">
          <div className="empty">
            <p className="text-muted">暂无 Webhook 订阅，点击「新建订阅」添加外部接收地址</p>
          </div>
        </div>
      ) : (
        subscriptions.map(sub => (
          <div key={sub.id} className="card mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <h3 className="mb-0">{sub.name}</h3>
                <span className={`badge ${sub.is_enabled ? 'badge-success' : 'badge-muted'}`}>
                  {sub.is_enabled ? '已启用' : '已停用'}
                </span>
              </div>
              <div className="flex gap-2">
                <button className="secondary" onClick={() => handleToggle(sub)}>
                  {sub.is_enabled ? '停用' : '启用'}
                </button>
                <button className="secondary" onClick={() => handleTest(sub)} disabled={testingId === sub.id}>
                  {testingId === sub.id ? <><Loader2 size={16} className="inline animate-spin mr-1" /> 发送中...</> : <><Send size={16} className="inline mr-1" /> 测试</>}
                </button>
                <button className="secondary" onClick={() => toggleDeliveries(sub)}>
                  <History size={16} className="inline mr-1" /> 投递记录
                </button>
                <button aria-label="删除" className="secondary" style={{ borderColor: 'var(--color-error)', color: 'var(--color-error)' }} onClick={() => handleDelete(sub)}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <p className="text-sm text-muted mb-2" style={{ wordBreak: 'break-all' }}>{sub.url}</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {(sub.events.length === 0 ? WEBHOOK_EVENTS : sub.events).map(event => (
                <span key={event} className="badge">{WEBHOOK_EVENT_LABELS[event] ?? event}</span>
              ))}
            </div>

            {deliveriesOpen[sub.id] && (
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th>事件</th>
                      <th>状态</th>
                      <th>尝试</th>
                      <th>HTTP</th>
                      <th>错误</th>
                      <th>投递时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(deliveries[sub.id] ?? []).length === 0 ? (
                      <tr><td colSpan={6} className="text-muted">暂无投递记录</td></tr>
                    ) : (
                      (deliveries[sub.id] ?? []).map(d => (
                        <tr key={d.id}>
                          <td>{d.event_type}</td>
                          <td className={statusColor(d.status)}>
                            {d.status === 'success' ? <CheckCircle2 size={14} className="inline mr-1" /> : d.status === 'failed' ? <XCircle size={14} className="inline mr-1" /> : <Clock size={14} className="inline mr-1" />}
                            {STATUS_LABELS[d.status] ?? d.status}
                          </td>
                          <td>{d.attempts}</td>
                          <td>{d.response_status ?? '-'}</td>
                          <td className="text-muted" style={{ maxWidth: 320 }}>{d.last_error || '-'}</td>
                          <td className="text-muted">{d.delivered_at ? new Date(d.delivered_at).toLocaleString() : '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
      )}
      {dialog}
    </div>
  );
}
